import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled, createTheme } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import BarChartIcon from '@mui/icons-material/BarChart';
import DescriptionIcon from '@mui/icons-material/Description';
import LayersIcon from '@mui/icons-material/Layers';
import Pagination from '@mui/material/Pagination';
import { AppProvider } from '@toolpad/core/AppProvider';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import { PageContainer } from '@toolpad/core/PageContainer';
import Grid from '@mui/material/Grid';
import {getAllOrderData, deleteOrderById, updateOrderById } from '../Api/order';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { BsGrid3X3GapFill } from "react-icons/bs";
import { HiUsers } from 'react-icons/hi2';
import { FaBasketShopping, FaFilePrescription } from "react-icons/fa6";
import IconButton from '@mui/material/IconButton';
import Papa from 'papaparse';
import MenuItem from '@mui/material/MenuItem';
import { IoMdMore } from "react-icons/io";
import Menu from '@mui/material/Menu';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LogoutIcon from '@mui/icons-material/Logout';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import { FaFileAlt, FaLocationArrow } from "react-icons/fa";
import Invoice from '../components/Invoice';
import { BiSolidCoupon } from 'react-icons/bi';

const loadScripts = () => {
  const html2canvasScript = document.createElement('script');
  html2canvasScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
  html2canvasScript.async = true;
  document.body.appendChild(html2canvasScript);

  const jsPDFScript = document.createElement('script');
  jsPDFScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
  jsPDFScript.async = true;
  document.body.appendChild(jsPDFScript);

  return () => {
    document.body.removeChild(html2canvasScript);
    document.body.removeChild(jsPDFScript);
  };
};

// Navigation configuration
const NAVIGATION = [
  {
    kind: 'header',
    title: 'Main items',
  },
  {
    segment: 'dashboard',
    title: 'Dashboard',
    icon: <DashboardIcon />,
  },
  {
    segment: 'dashboard-products',
    title: 'Products',
    icon: <FaBasketShopping style={{ fontSize: '22px' }} />,
  },
  {
    segment: 'dashboard-users',
    title: 'Users',
    icon: <HiUsers style={{ fontSize: '22px' }} />,
  },
  {
    segment: 'dashboard-categories',
    title: 'Categories',
    icon: <BsGrid3X3GapFill style={{ fontSize: '22px' }} />,
  },
  {
    segment: 'dashboard-orders',
    title: 'Orders',
    icon: <ShoppingCartIcon />,
  },
  {
    segment: 'prescriptions',
    title: 'Prescriptions',
    icon: <FaFilePrescription style={{ fontSize: '20px' }} />,
  },
  {
    segment: 'dashboard-coupons',
    title: 'Coupons',
    icon: <BiSolidCoupon style={{ fontSize: '22px' }} />,
  },
  {
    segment: 'dashboard-content',
    title: 'Content',
    icon: <FaFileAlt style={{ fontSize: '20px' }} />,
  },
  {
    segment: 'dashboard-location',
    title: 'Location',
    icon: <FaLocationArrow />,
  },
  {
    kind: 'divider',
  },
  {
    kind: 'header',
    title: 'Analytics',
  },
  {
    segment: 'reports',
    title: 'Reports',
    icon: <BarChartIcon />,
    children: [
      {
        segment: 'sales',
        title: 'Sales',
        icon: <DescriptionIcon />,
      },
      {
        segment: 'traffic',
        title: 'Traffic',
        icon: <DescriptionIcon />,
      },
    ],
  },
  {
    segment: 'integrations',
    title: 'Integrations',
    icon: <LayersIcon />,
  },
];

// Styled Skeleton component
const Skeleton = styled('div')(({ theme, height }) => ({
  backgroundColor: theme.palette.action.hover,
  borderRadius: 'inherit',
  height,
}));

// Custom router hook
function useDemoRouter(initialPath) {
  const [pathname, setPathname] = React.useState(initialPath);
  const navigate = useNavigate();

  const router = React.useMemo(() => {
    return {
      pathname,
      searchParams: new URLSearchParams(),
      navigate: (path) => {
        setPathname(String(path));
        navigate(path);
      },
    };
  }, [pathname, navigate]);

  return router;
}

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  border: "1px solid transparent",
  borderRadius: 1,
  boxShadow: 24,
  p: 4,
};

// Create a light theme
const theme = createTheme({
  palette: {
    mode: 'light',
  },
});

function DshOrders({ onLogout }) {
  const navigate = useNavigate();
  const router = useDemoRouter('/dashboard-orders');
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 30;
  const [auth, setAuth] = React.useState(true);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  // Set while an invoice is being rendered to PDF; drives the two-phase capture
  // in the effect below (BUG-4).
  const [pendingInvoiceId, setPendingInvoiceId] = useState(null);
  const [openEditModal, setOpenEditModal] = React.useState(false);
  const [openViewModal, setOpenViewModal] = React.useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [sortStatus, setSortStatus] = useState('all')
  const [orderStatus, setOrderStatus] = useState('');
  const [orderPaymentStatus, setOrderPaymentStatus] = useState('paid');
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const [orderFilter, setOrderFilter] = useState('all');
  const invoiceRef = useRef(null);

  const handleEditModalOpen = () => setOpenEditModal(true);
  const handleEditModalClose = () => setOpenEditModal(false);
  const handleViewModalOpen = () => setOpenViewModal(true);
  const handleViewModalClose = () => setOpenViewModal(false);

  const handleMenu = (event, order) => {
    setAnchorEl(event.currentTarget);
    setSelectedOrder(order);
    // BUG-4: this was never set anywhere except to null, so `selectedOrderId` was
    // permanently null. That broke two things at once — the hidden invoice div is
    // gated on it so it never rendered, and `deleteOrder` passed null as the id.
    setSelectedOrderId(order?.id ?? null);
  };

  // Closes the menu only. It used to also null `selectedOrderId`, and
  // `deleteOrder`/`downloadInvoice` both call this *before* reading that id —
  // so the value was always cleared a moment before it was needed.
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (event) => {
    setAuth(event.target.checked);
  };

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const response = await getAllOrderData(page, itemsPerPage);
        setOrders(Array.isArray(response.data) ? response.data : []);
        setTotalPages(Number(response.totalPages));
      } catch (error) {
        console.error('Error fetching orders:', error);
        setOrders([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [page, token]);

  useEffect(() => {
    const cleanup = loadScripts();
    return cleanup;
  }, []);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const downloadCSV = () => {
    if (!orders || orders.length === 0) {
      alert("No order data to download.");
      return;
    }

    const now = new Date();
    let filteredOrders = [...orders];

    if (orderFilter === 'today') {
      filteredOrders = filteredOrders.filter(order => {
        const orderDate = new Date(order.date_created);
        return (
          orderDate.getDate() === now.getDate() &&
          orderDate.getMonth() === now.getMonth() &&
          orderDate.getFullYear() === now.getFullYear()
        );
      });
    } else if (orderFilter === 'last7days') {
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(now.getDate() - 7);
      filteredOrders = filteredOrders.filter(order => {
        const orderDate = new Date(order.date_created);
        return orderDate >= sevenDaysAgo && orderDate <= now;
      });
    } else if (orderFilter === 'thismonth') {
      filteredOrders = filteredOrders.filter(order => {
        const orderDate = new Date(order.date_created);
        return (
          orderDate.getMonth() === now.getMonth() &&
          orderDate.getFullYear() === now.getFullYear()
        );
      });
    }

    if (filteredOrders.length === 0) {
      alert("No orders match the selected filter.");
      return;
    }

    const csvData = filteredOrders.map(order => ({
      ID: order.id,
      PurchaseDate: new Date(order.date_created).toLocaleDateString(),
      Name: `${order.billing?.first_name || ''} ${order.billing?.last_name || ''}`,
      Phone: order.billing?.phone || '',
      Address: order.billing?.address_1 || '',
      Total: order.total || '',
      PaymentMethod: order.payment_method_title || order.payment_method || '',
      PaymentStatus: order.needs_payment ? 'Not Paid' : 'Paid',
      PrescriptionID: order.meta_data?.find(meta => meta.key === 'prescription_id')?.value || '',
      UPITransactionID: order.meta_data?.find(meta => meta.key === 'upi_transaction_id')?.value || '',
      Status: order.status || '',
    }));

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'orders.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  async function deleteOrder() {
    // Read the id before closing the menu, and name the order in the prompt —
    // "delete order?" with no identifier is not something you can confirm safely.
    const orderId = selectedOrder?.id ?? selectedOrderId;
    handleClose();

    if (!orderId) {
      alert('No order selected. Please reopen the menu and try again.');
      return;
    }

    if (window.confirm(`Delete order #${orderId}? This cannot be undone.`)) {
      setLoading(true);
      try {
        await deleteOrderById(orderId, token);

        const response = await getAllOrderData(page, itemsPerPage);

        setOrders(Array.isArray(response.data) ? response.data : []);
        setTotalPages(Number(response.totalPages));
      } catch (error) {
        console.error('Error deleting order:', error);
        // Previously swallowed into the console: the list refetched unchanged, so
        // a failed delete looked indistinguishable from a successful one.
        alert('Failed to delete the order. It has not been removed.');
      } finally {
        setLoading(false);
      }
    }
  }

  async function viewOrder(id) {
    handleClose();
    handleViewModalOpen();
    const order = orders.find(o => o.id === id);
    setSelectedOrder(order);
  }

  function handleOpenEditModal() {
    if (!selectedOrder) return;

    setOrderStatus(selectedOrder.status);
    setOrderPaymentStatus(
      selectedOrder.needs_payment ? 'not_paid' : 'paid'
    );

    setOpenEditModal(true);
    handleClose();
  }

  async function updateOrderFromModal() {
    setLoading(true);

    try {
      const data = {
        status: orderStatus,
         set_paid: orderPaymentStatus === 'paid',
      
      };

      if (!selectedOrder) {
        console.error("No order selected");
        return;
      }

      await updateOrderById(selectedOrder.id, data);

      const response = await getAllOrderData(page, itemsPerPage);
      setOrders(Array.isArray(response.data) ? response.data : []);
      setTotalPages(Number(response.totalPages));

      handleEditModalClose(); // close after success
    } catch (error) {
      console.error('Error updating order:', error);
    } finally {
      setLoading(false);
    }
  }

  // Two-phase, and it has to be: the invoice markup is rendered into a hidden div
  // that only exists while `selectedOrderId` is set, so the node cannot be
  // captured in the same tick that asks for it. This selects the order, lets React
  // commit that render, and only then rasterises. The old single-pass version read
  // `invoiceRef.current` while it was still null and always failed.
  function downloadInvoice(id) {
    const order = orders.find(o => o.id === id);
    handleClose();

    if (!order) {
      alert('Order not found. Please refresh and try again.');
      return;
    }

    setSelectedOrder(order);
    setSelectedOrderId(id);
    setPendingInvoiceId(id);
  }

  useEffect(() => {
    if (!pendingInvoiceId) return;

    let cancelled = false;

    async function render() {
      try {
        if (!window.html2canvas || !window.jspdf) {
          throw new Error('Required libraries (html2canvas or jsPDF) are not loaded');
        }

        // Wait for the browser to paint the hidden div before measuring it.
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        if (cancelled) return;

        const element = invoiceRef.current;
        if (!element) {
          throw new Error('Invoice markup was not rendered');
        }

        await rasteriseInvoice(element, pendingInvoiceId);
      } catch (error) {
        console.error('Error generating invoice:', error);
        alert(`Failed to generate invoice: ${error.message}`);
      } finally {
        if (!cancelled) {
          setPendingInvoiceId(null);
        }
      }
    }

    render();
    return () => { cancelled = true; };
  }, [pendingInvoiceId]);

  // Rasterise the already-rendered invoice node into a PDF. Errors propagate to
  // the caller, which owns the reporting — this used to swallow them into a
  // generic "please try again" that hid the actual cause (a null element).
  async function rasteriseInvoice(element, id) {
    const { jsPDF } = window.jspdf;

    const canvas = await window.html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
    heightLeft -= pdfHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
      heightLeft -= pdfHeight;
    }

    pdf.save(`invoice_${id}.pdf`);
  }

  const filteredOrders = orders.filter(order => {
    if (sortStatus === 'all') return true;
    return order.status === sortStatus;
  });

  const branding = {
    logo: (
      <div className="header-logo">
        <img onClick={() => navigate('/')} src={require('../images/logo.png')} alt="" style={{ height: '52px', width: '60px' }} />
      </div>
    ),
    title: '',
  };

  const handleLogoutClick = () => {
    setOpenLogoutDialog(true);
  };

  const handleLogoutConfirm = () => {
    setOpenLogoutDialog(false);
    onLogout();
    navigate('/');
  };

  const handleLogoutCancel = () => {
    setOpenLogoutDialog(false);
  };

  const ToolbarActions = () => (
    <Button
      variant="outlined"
      startIcon={<LogoutIcon />}
      onClick={handleLogoutClick}
      sx={{ marginRight: 2 }}
      style={{ background: "#0165AB", color: "#fff" }}
    >
      Logout
    </Button>
  );

  return (
    <AppProvider
      navigation={NAVIGATION}
      router={router}
      theme={theme}
      branding={branding}
    >
      <DashboardLayout
        slots={{
          toolbarActions: ToolbarActions,
        }}
      >
        <PageContainer>
          {loading ? (
            <Grid container spacing={1}>
              <Grid size={5} />
              <Grid size={12}>
                <Skeleton height={14} />
              </Grid>
              <Grid size={12}>
                <Skeleton height={14} />
              </Grid>
              <Grid size={4}>
                <Skeleton height={100} />
              </Grid>
              <Grid size={8}>
                <Skeleton height={100} />
              </Grid>
              <Grid size={12}>
                <Skeleton height={150} />
              </Grid>
              <Grid size={12}>
                <Skeleton height={14} />
              </Grid>
              <Grid size={3}>
                <Skeleton height={100} />
              </Grid>
              <Grid size={3}>
                <Skeleton height={100} />
              </Grid>
              <Grid size={3}>
                <Skeleton height={100} />
              </Grid>
              <Grid size={3}>
                <Skeleton height={100} />
              </Grid>
            </Grid>
          ) : (
            <section className="admin-dashboard">
              <Grid container spacing={1}>
                <Grid size={12} height={5} />
                <Grid size={6} height={35} />
                <Grid size={2} height={35}>
                  <div className="d-flex" style={{ width: '100%', height: '100%', justifyContent: 'space-between' }}>
                    <span style={{ padding: '5px 0 0' }}>Sort:</span>
                    <select
                      value={sortStatus}
                      onChange={(e) => setSortStatus(e.target.value)}
                      style={{
                        height: "100%",
                        width: "80%",
                        borderRadius: "5px",
                        border: "1px solid #ccc",
                        padding: "5px"
                      }}
                    >
                      <option value="all">All</option>
                      <option value="pending">Pending</option>
                      <option value="processing">Processing</option>
                      <option value="cancelled">Cancelled</option>
                    </select>

                  </div>
                </Grid>
                <Grid size={2} height={35}>
                  <select
                    value={orderFilter}
                    onChange={(e) => setOrderFilter(e.target.value)}
                    style={{
                      height: "100%",
                      width: "100%",
                      borderRadius: "5px",
                      border: "1px solid #ccc",
                      padding: "5px"
                    }}
                  >
                    <option value="all">All Orders</option>
                    <option value="today">Today's Orders</option>
                    <option value="last7days">Last 7 Days</option>
                    <option value="thismonth">This Month</option>
                  </select>
                </Grid>
                <Grid size={2} height={35}>
                  <button
                    style={{
                      height: "100%",
                      width: "100%",
                      border: "1px solid transparent",
                      background: "#0165AB",
                      color: "#fff",
                      borderRadius: "5px",
                      cursor: "pointer",
                    }}
                    onClick={downloadCSV}
                  >
                    Download Order Data
                  </button>
                </Grid>
                <Grid size={12}>
                  <table className="user-detail-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Purchase Date(M/D/Y)</th>
                        <th>Name</th>
                        <th>Phone</th>
                        <th>Address</th>
                        <th>Total</th>
                        <th>Payment</th>
                        <th>Payment Status</th>
                        <th>Prescription</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.length > 0 ? (
                        filteredOrders.map((order) => {
                          const prescriptionId = order.meta_data?.find(
                            (meta) => meta.key === 'prescription_id'
                          )?.value || '';
                          const upiTransactionId = order.meta_data?.find(
                            (meta) => meta.key === 'upi_transaction_id'
                          )?.value || '';
                          return (
                            <tr key={order.id}>
                              <td>{order.id}</td>
                              <td>{new Date(order.date_created).toLocaleDateString()}</td>
                              <td>{order.billing?.first_name} {order.billing?.last_name}</td>
                              <td>{order.billing?.phone}</td>
                              <td>{order.billing?.address_1}</td>
                              <td>{order.total}</td>
                              <td>{order.payment_method_title || order.payment_method}</td>
                              <td>{order.needs_payment === true ? 'Not Paid' : 'Paid'}</td>
                              <td>
                                {prescriptionId ? (
                                  <span>Prescription ID: {prescriptionId}</span>
                                ) : upiTransactionId ? (
                                  <span>UPI Transaction ID: {upiTransactionId}</span>
                                ) : (
                                  'None'
                                )}
                              </td>
                              <td>{order.status}</td>
                              <td>
                                {auth && (
                                  <div>
                                    <IconButton
                                      size="large"
                                      aria-label="account of current user"
                                      aria-controls="menu-appbar"
                                      aria-haspopup="true"
                                      onClick={(event) => handleMenu(event, order)}
                                      color="inherit"
                                    >
                                      <IoMdMore style={{ fontSize: "20px" }} />
                                    </IconButton>
                                    <Menu
                                      id="menu-appbar"
                                      anchorEl={anchorEl}
                                      anchorOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                      }}
                                      keepMounted
                                      transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                      }}
                                      open={Boolean(anchorEl)}
                                      onClose={handleClose}
                                    >
                                      <MenuItem onClick={handleOpenEditModal}>Edit</MenuItem>
                                      <MenuItem onClick={deleteOrder}>Remove</MenuItem>
                                      <MenuItem onClick={() => viewOrder(order.id)}>View</MenuItem>
                                      <MenuItem onClick={() => downloadInvoice(order.id)}>Invoice</MenuItem>
                                      {/* BUG-5: an "Assign" item used to sit here whose only
                                          action was to close the menu. Removed rather than
                                          left in place — a control that looks functional and
                                          silently does nothing is worse than its absence,
                                          and there is no assignment feature to wire it to.
                                          If order assignment is wanted, it needs a real
                                          design (assign to whom? stored where?). */}
                                    </Menu>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="11">No orders found!</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Grid>
                <Modal
                  open={openEditModal}
                  onClose={handleEditModalClose}
                  aria-labelledby="modal-modal-title"
                  aria-describedby="modal-modal-description"
                >
                  <Box sx={{ ...style, width: 400 }}>
                    <Typography id="modal-modal-title" variant="h6" component="h2">
                      Edit Order
                    </Typography>
                    <Box id="modal-modal-description" sx={{ mt: 2 }}>
                      <form>
                        <div className="product-input-groups">
                          <label htmlFor="status">Status</label>
                          <br />
                          <select
                            onChange={(e) => setOrderStatus(e.target.value)}
                            id="status"
                            name="status"
                            required
                            style={{ height: "30px", width: "100%", margin: "0 0 20px" }}
                            value={orderStatus}
                          >
                            <option value="pending">Pending Payment</option>
                            <option value="processing">Processing</option>
                            <option value="on-hold">On Hold</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="refunded">Refunded</option>
                            <option value="failed">Failed</option>
                            <option value="draft">Draft</option>
                          </select>

                        </div>
                        <div className="product-input-groups">
                          <label htmlFor="payment_status">Payment Status</label>
                          <br />
                          <select
                            onChange={(e) => setOrderPaymentStatus(e.target.value)}
                            id="payment_status"
                            name="payment_status"
                            required
                            style={{ height: "30px", width: "100%", margin: "0 0 20px" }}
                            value={orderPaymentStatus}
                          >
                            <option value="paid">Paid</option>
                            <option value="not_paid">Not Paid</option>
                          </select>
                        </div>
                        <div className="d-flex">
                          <button
                            style={{
                              height: "35px",
                              width: "100px",
                              border: "1px solid transparent",
                              background: "red",
                              color: "#fff",
                              borderRadius: "5px",
                              cursor: "pointer",
                            }}
                            type="button"
                            onClick={handleEditModalClose}
                          >
                            Cancel
                          </button>
                          <button
                            style={{
                              height: "35px",
                              width: "100px",
                              margin: "0 0 0 20px",
                              border: "1px solid transparent",
                              background: "#0165AB",
                              color: "#fff",
                              borderRadius: "5px",
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              updateOrderFromModal();
                            }}
                          >
                            Update
                          </button>
                        </div>
                      </form>
                    </Box>
                  </Box>
                </Modal>
                <Modal
                  open={openViewModal}
                  onClose={handleViewModalClose}
                  aria-labelledby="view-modal-title"
                  aria-describedby="view-modal-description"
                >
                  <Box sx={{ ...style, maxHeight: '80vh', overflow: 'auto' }}>
                    <Typography id="view-modal-title" variant="h6" component="h2">
                      Order Details - #{selectedOrder?.id || ''}
                    </Typography>
                    {selectedOrder && (
                      <Box id="view-modal-description" sx={{ mt: 2 }}>
                        <Typography variant="body1">
                          <strong>Purchase Date:</strong> {new Date(selectedOrder.date_created).toLocaleString()}
                        </Typography>
                        <Typography variant="body1">
                          <strong>Customer Name:</strong> {selectedOrder.billing?.first_name} {selectedOrder.billing?.last_name}
                        </Typography>
                        <Typography variant="body1">
                          <strong>Phone:</strong> {selectedOrder.billing?.phone}
                        </Typography>
                        <Typography variant="body1">
                          <strong>Address:</strong> {selectedOrder.billing?.address_1}, {selectedOrder.billing?.city}, {selectedOrder.billing?.state} {selectedOrder.billing?.postcode}, {selectedOrder.billing?.country}
                        </Typography>
                        <Typography variant="body1">
                          <strong>Total:</strong> {selectedOrder.total}
                        </Typography>
                        <Typography variant="body1">
                          <strong>Payment Method:</strong> {selectedOrder.payment_method_title || selectedOrder.payment_method}
                        </Typography>
                        <Typography variant="body1">
                          <strong>Payment Status:</strong> {selectedOrder.needs_payment ? 'Not Paid' : 'Paid'}
                        </Typography>
                        <Typography variant="body1">
                          <strong>Prescription/UPI:</strong> {
                            selectedOrder.meta_data?.find(m => m.key === 'prescription_id')?.value ?
                              `Prescription ID: ${selectedOrder.meta_data.find(m => m.key === 'prescription_id').value}` :
                              selectedOrder.meta_data?.find(m => m.key === 'upi_transaction_id')?.value ?
                                `UPI Transaction ID: ${selectedOrder.meta_data.find(m => m.key === 'upi_transaction_id').value}` :
                                'None'
                          }
                        </Typography>
                        <Typography variant="body1">
                          <strong>Order Status:</strong> {selectedOrder.status}
                        </Typography>
                        <Typography variant="subtitle1" sx={{ mt: 2 }}>
                          <strong>Order Items:</strong>
                        </Typography>
                        {selectedOrder.line_items?.map((item) => (
                          <Box key={item.id} sx={{ ml: 2, mb: 1 }}>
                            <Typography variant="body2">
                              - {item.name} (SKU: {item.sku || 'N/A'})<br />
                              Quantity: {item.quantity}, Price: {item.price}, Subtotal: {item.subtotal}
                            </Typography>
                          </Box>
                        )) || <Typography variant="body2">No items found</Typography>}
                      </Box>
                    )}
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button onClick={handleViewModalClose}>Close</Button>
                    </Box>
                  </Box>
                </Modal>
                <Grid size={12} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Grid>
              </Grid>
            </section>
          )}
          {/* Hidden div to render the invoice for PDF generation */}
          {selectedOrderId && (
            <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
              <div ref={invoiceRef}>
                <Invoice order={selectedOrder} />
              </div>
            </div>
          )}
        </PageContainer>
      </DashboardLayout>
      <Dialog
        open={openLogoutDialog}
        onClose={handleLogoutCancel}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
      >
        <DialogTitle id="logout-dialog-title">Confirm Logout</DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            Are you sure you want to log out?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel}>Cancel</Button>
          <Button onClick={handleLogoutConfirm} autoFocus>
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </AppProvider>
  );
}

export default DshOrders;